// Multi-select dropdown for source-system filtering.
//
// Replaces the previous chip row to keep horizontal space tight when the
// platform onboards more sources (Shopify, Stripe, Klaviyo, etc.). Same
// shape the production FE will eventually need - the CSV the API expects
// (`?source=magento,bq_ga4`) maps directly to the selected set.
//
// Selection model:
//   - `selected: Set<string>` is the source of truth.
//   - Empty set ≡ "All sources" (no filter applied).
//   - Non-empty set ≡ exactly those sources.
//
// Dismiss-on-outside-click is wired on the document; we stop event
// propagation inside the menu so clicks on checkboxes don't close it.

import { useEffect, useRef, useState } from "react";
import { formatSource } from "../format";

export function SourceDropdown({
  available,
  selected,
  onChange,
}: {
  available: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click. Bind only when open to avoid a permanent listener.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const isAll = selected.size === 0 || selected.size === available.length;
  const label =
    isAll
      ? "All sources"
      : selected.size === 1
        ? `Source: ${formatSource([...selected][0])}`
        : `Sources (${selected.size})`;

  function toggleAll() {
    onChange(new Set()); // empty = all
  }

  function toggleOne(source: string) {
    // Start from "current effective set". When selected is empty (= all),
    // expand to the full list before toggling - that way unchecking one
    // source from "all" leaves the other(s) checked, which is the standard
    // multi-select expectation.
    const base = selected.size === 0 ? new Set(available) : new Set(selected);
    if (base.has(source)) base.delete(source);
    else base.add(source);
    // Normalise: if the user has re-selected all, collapse back to the
    // "empty = all" representation.
    if (base.size === available.length) base.clear();
    onChange(base);
  }

  return (
    <div className="dropdown" ref={rootRef}>
      <button
        type="button"
        className={"chip dropdown-trigger" + (isAll ? "" : " active")}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {label}
        <span className="dropdown-caret" aria-hidden>▾</span>
      </button>

      {open && (
        <div className="dropdown-menu" role="menu">
          <label className="dropdown-item dropdown-item-all">
            <input type="checkbox" checked={isAll} onChange={toggleAll} />
            <span>All sources</span>
          </label>
          {available.length > 0 && <hr className="dropdown-divider" />}
          {available.map((s) => {
            const checked = isAll || selected.has(s);
            return (
              <label key={s} className="dropdown-item">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(s)}
                />
                <span className="dropdown-source-name">{formatSource(s)}</span>
              </label>
            );
          })}
          {available.length === 0 && (
            <div className="dropdown-empty">No sources yet</div>
          )}
        </div>
      )}
    </div>
  );
}
